/**
 * WidgetManager - Coordinates state between multiple chat widgets
 * 
 * This singleton class manages the co-existence of multiple chat widgets,
 * ensuring only one widget is active at a time and handling transitions
 * between different chat modes.
 */

export type WidgetType = 'voice' | 'text' | 'none';

export interface WidgetState {
    activeWidget: WidgetType;
    isFloatingButtonVisible: boolean;
    transitionInProgress: boolean;
}

export interface WidgetManagerConfig {
    /**
     * Called when widget state changes
     * @param newState - The new widget state
     */
    onStateChange?: (newState: WidgetState) => void;

    /**
     * Called when a widget requests to open
     * @param widgetType - The type of widget requesting to open
     * @returns Whether the widget is allowed to open
     */
    onWidgetOpenRequest?: (widgetType: WidgetType) => boolean;

    /**
     * Called when transitioning between widgets
     * @param from - The widget being closed
     * @param to - The widget being opened
     */
    onWidgetTransition?: (from: WidgetType, to: WidgetType) => void;

    /**
     * Delay in ms between closing one widget and opening another
     */
    transitionDelay?: number;
}

class WidgetManager {
    private static instance: WidgetManager | null = null;
    private state: WidgetState = {
        activeWidget: 'none',
        isFloatingButtonVisible: true,
        transitionInProgress: false
    };
    private config: WidgetManagerConfig = {};
    private subscribers: Set<(state: WidgetState) => void> = new Set();
    private transitionTimeout: NodeJS.Timeout | null = null;

    /**
     * Private constructor to enforce singleton pattern
     */
    private constructor() { }

    /**
     * Get the singleton instance of WidgetManager
     * @returns The WidgetManager instance
     */
    static getInstance(): WidgetManager {
        if (!WidgetManager.instance) {
            WidgetManager.instance = new WidgetManager();
        }
        return WidgetManager.instance;
    }

    /**
     * Configure the widget manager
     * @param config - Configuration options for the widget manager
     */
    configure(config: WidgetManagerConfig): void {
        this.config = { ...this.config, ...config };
    }

    /**
     * Subscribe to state changes
     * @param callback - Function to call when state changes
     * @returns Unsubscribe function
     */
    subscribe(callback: (state: WidgetState) => void): () => void {
        this.subscribers.add(callback);
        // Immediately call with current state
        callback(this.state);

        // Return unsubscribe function
        return () => {
            this.subscribers.delete(callback);
        };
    }

    /**
     * Get current widget state
     * @returns The current widget state
     */
    getState(): WidgetState {
        return { ...this.state };
    }

    /**
     * Request to open a widget
     * @param widgetType - The type of widget to open
     * @returns Whether the request was successful
     */
    async requestOpen(widgetType: WidgetType): Promise<boolean> {
        // Don't allow operations during transitions
        if (this.state.transitionInProgress) {
            return false;
        }

        // If the same widget is already active, do nothing
        if (this.state.activeWidget === widgetType) {
            return true;
        }

        // Check if opening is allowed
        if (this.config.onWidgetOpenRequest) {
            const allowed = this.config.onWidgetOpenRequest(widgetType);
            if (!allowed) {
                return false;
            }
        }

        // If another widget is active, close it first
        if (this.state.activeWidget !== 'none') {
            await this.transitionBetweenWidgets(this.state.activeWidget, widgetType);
        } else {
            // Direct open
            this.updateState({
                activeWidget: widgetType,
                isFloatingButtonVisible: true
            });
        }

        return true;
    }

    /**
     * Request to close a widget
     * @param widgetType - The type of widget to close
     * @returns Whether the request was successful
     */
    requestClose(widgetType: WidgetType): boolean {
        // Don't allow operations during transitions
        if (this.state.transitionInProgress) {
            return false;
        }

        // Only close if this widget is actually active
        if (this.state.activeWidget !== widgetType) {
            return false;
        }

        this.updateState({
            activeWidget: 'none',
            isFloatingButtonVisible: true
        });

        return true;
    }

    /**
     * Toggle the active widget
     * @returns The new active widget type
     */
    toggleWidget(): WidgetType {
        if (this.state.transitionInProgress) {
            return this.state.activeWidget;
        }

        if (this.state.activeWidget === 'none') {
            // Default to voice widget when opening
            this.requestOpen('voice');
            return 'voice';
        } else {
            // Close current widget
            const currentWidget = this.state.activeWidget;
            this.requestClose(currentWidget);
            return 'none';
        }
    }

    /**
     * Handle transition between widgets
     * @param from - The widget being closed
     * @param to - The widget being opened
     */
    private async transitionBetweenWidgets(from: WidgetType, to: WidgetType): Promise<void> {
        // Clear any existing transition
        if (this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
        }

        // Mark transition as in progress
        this.updateState({ transitionInProgress: true });

        // Notify about transition
        this.config.onWidgetTransition?.(from, to);

        // Close current widget
        this.updateState({ activeWidget: 'none' });

        // Wait for transition delay
        const delay = this.config.transitionDelay ?? 300;
        await new Promise(resolve => {
            this.transitionTimeout = setTimeout(resolve, delay);
        });

        // Open new widget
        this.updateState({
            activeWidget: to,
            transitionInProgress: false
        });
    }

    /**
     * Set floating button visibility
     * @param visible - Whether the floating button should be visible
     */
    setFloatingButtonVisibility(visible: boolean): void {
        this.updateState({ isFloatingButtonVisible: visible });
    }

    /**
     * Force close all widgets
     */
    closeAll(): void {
        if (this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
        }

        this.updateState({
            activeWidget: 'none',
            isFloatingButtonVisible: true,
            transitionInProgress: false
        });
    }

    /**
     * Update state and notify subscribers
     * @param updates - Partial state updates
     */
    private updateState(updates: Partial<WidgetState>): void {
        const oldState = { ...this.state };
        this.state = { ...this.state, ...updates };

        // Only notify if state actually changed
        if (JSON.stringify(oldState) !== JSON.stringify(this.state)) {
            this.config.onStateChange?.(this.state);
            this.subscribers.forEach(callback => callback(this.state));
        }
    }

    /**
     * Reset the widget manager (useful for testing)
     */
    reset(): void {
        if (this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
        }

        this.state = {
            activeWidget: 'none',
            isFloatingButtonVisible: true,
            transitionInProgress: false
        };
        this.subscribers.clear();
        this.config = {};
    }
}

// Export singleton instance
export const widgetManager = WidgetManager.getInstance();
